import { useCallback, useEffect, useState } from 'react';
import { showToast } from '../../../../components/toast-store';
import { getApiErrorMessage } from '../../../../lib/result-codes';
import * as businessProfileApi from '../BusinessProfile.api';
import { BUSINESS_PROFILE_NOTIFICATION_MESSAGES } from '../BusinessProfile.const';
import { BusinessAddressUIState, BusinessCategory, BusinessHoursUIState, BusinessProfileUIState, Organization, mapApiResponseToUI } from '../BusinessProfile.data';

export const useBusinessProfile = () => {
    const [profile, setProfile] = useState<BusinessProfileUIState | null>(null);
    const [categories, setCategories] = useState<BusinessCategory[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadProfile = useCallback(async () => {
        try {
            setIsLoading(true); setError(null);
            setProfile(mapApiResponseToUI(await businessProfileApi.fetchBusinessProfile()));
        } catch (loadError: unknown) {
            setError(getApiErrorMessage(loadError, BUSINESS_PROFILE_NOTIFICATION_MESSAGES.FETCH_ERROR));
            showToast({ type: 'error', title: 'Error', message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.FETCH_ERROR });
        } finally { setIsLoading(false); }
    }, []);

    useEffect(() => {
        void loadProfile();
        void businessProfileApi.fetchBusinessCategories().then(setCategories).catch(() => setCategories([]));
        void businessProfileApi.fetchOrganizations().then(setOrganizations).catch(() => setOrganizations([]));
    }, [loadProfile]);

    const updateProfileField = useCallback((update: Partial<BusinessProfileUIState>) => setProfile((current) => current ? { ...current, ...update } : current), []);
    const addAddress = useCallback((address: BusinessAddressUIState) => setProfile((current) => current ? { ...current, addresses: [...current.addresses, address] } : current), []);
    const updateAddress = useCallback((index: number, address: BusinessAddressUIState) => setProfile((current) => current ? { ...current, addresses: current.addresses.map((item, itemIndex) => itemIndex === index ? address : item) } : current), []);
    const updateBusinessHours = useCallback((day: string, hours: BusinessHoursUIState) => setProfile((current) => current ? { ...current, business_hours: current.business_hours.map((item) => item.day === day ? hours : item) } : current), []);
    const updateSocialMedia = useCallback((update: Partial<BusinessProfileUIState['social_media']>) => setProfile((current) => current ? { ...current, social_media: { ...current.social_media, ...update } } : current), []);
    const uploadLogo = useCallback(async (file: File) => {
        try {
            const { logo_url } = await businessProfileApi.uploadBusinessLogo(file);
            setProfile((current) => current ? { ...current, logo_url, logo_file: null } : current);
            showToast({ type: 'success', title: 'Success', message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.UPLOAD_LOGO_SUCCESS });
        } catch (uploadError: unknown) {
            showToast({ type: 'error', title: 'Error', message: getApiErrorMessage(uploadError, BUSINESS_PROFILE_NOTIFICATION_MESSAGES.UPLOAD_LOGO_ERROR) });
        }
    }, []);
    const saveProfile = useCallback(async () => {
        if (!profile) return;
        try {
            setIsSaving(true); setError(null);
            setProfile(mapApiResponseToUI(await businessProfileApi.updateBusinessProfile(profile)));
            showToast({ type: 'success', title: 'Success', message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.SAVE_SUCCESS });
        } catch (saveError: unknown) {
            setError(getApiErrorMessage(saveError, BUSINESS_PROFILE_NOTIFICATION_MESSAGES.SAVE_ERROR));
            showToast({ type: 'error', title: 'Error', message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.SAVE_ERROR });
        } finally { setIsSaving(false); }
    }, [profile]);

    return { profile, categories, organizations, isLoading, isSaving, error, saveProfile, updateProfileField, addAddress, updateAddress, updateBusinessHours, updateSocialMedia, uploadLogo };
};
